import { useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { CalendarIcon, Clock, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PACKAGES } from "@/data";
import Reveal from "@/components/Reveal";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Booking({ selectedPackage, setSelectedPackage }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", aircraft: "", message: "" });
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const TIME_SLOTS = [
    "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
    "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM",
  ];

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedPackage) return toast.error("Please select a package.");
    if (!date) return toast.error("Please choose a preferred date.");
    if (!time) return toast.error("Please choose a preferred time.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        ...form,
        package: selectedPackage,
        preferred_date: `${format(date, "yyyy-MM-dd")} at ${time}`,
      });
      toast.success(data.message || "Booking request received.");
      setForm({ name: "", email: "", phone: "", aircraft: "", message: "" });
      setDate(null);
      setTime("");
      setSelectedPackage("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="booking" className="relative bg-ink py-28 md:py-40 border-t border-white/10" data-testid="booking-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-4">
          <Reveal>
            <span className="overline">[ Request a slot ]</span>
            <h2 className="mt-6 font-display font-semibold tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Book your <span className="text-lime">detail</span> now!
            </h2>
            <p className="mt-8 text-white/55 font-body max-w-sm">
              Tell us about your aircraft and preferred date. We&apos;ll confirm availability and
              a precise quote within 24 hours.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-8">
          <form onSubmit={submit} data-testid="booking-form" className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
            <div>
              <label className="overline block mb-2">Full name</label>
              <input data-testid="booking-name" required value={form.name} onChange={update("name")} className="clinical-input" placeholder="Your name" />
            </div>
            <div>
              <label className="overline block mb-2">Email</label>
              <input data-testid="booking-email" required type="email" value={form.email} onChange={update("email")} className="clinical-input" placeholder="you@email.com" />
            </div>
            <div>
              <label className="overline block mb-2">Phone</label>
              <input data-testid="booking-phone" required value={form.phone} onChange={update("phone")} className="clinical-input" placeholder="Contact number" />
            </div>
            <div>
              <label className="overline block mb-2">Aircraft</label>
              <input data-testid="booking-aircraft" value={form.aircraft} onChange={update("aircraft")} className="clinical-input" placeholder="e.g. Cessna 172, Gulfstream G550" />
            </div>

            <div>
              <label className="overline block mb-2">Package</label>
              <Select value={selectedPackage} onValueChange={setSelectedPackage}>
                <SelectTrigger data-testid="booking-package" className="rounded-none bg-transparent border-0 border-b border-white/10 px-0 h-auto py-3 text-white focus:ring-0 focus:border-lime">
                  <SelectValue placeholder="Select cabin class" />
                </SelectTrigger>
                <SelectContent className="bg-surface border-white/10 text-white rounded-none">
                  {PACKAGES.map((p) => (
                    <SelectItem key={p.key} value={p.key} data-testid={`booking-package-opt-${p.key}`} className="font-mono text-sm">
                      {p.key}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="overline block mb-2">Preferred date &amp; time</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="booking-date"
                    className="clinical-input flex items-center justify-between text-left"
                  >
                    <span className={date ? "text-white" : "text-white/40"}>
                      {date ? format(date, "PPP") : "Pick a date"}
                    </span>
                    <CalendarIcon size={16} className="text-lime" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-surface border-white/10 rounded-none" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              <Select value={time} onValueChange={setTime}>
                <SelectTrigger data-testid="booking-time" className="mt-4 clinical-input flex items-center justify-between text-left [&>svg]:hidden">
                  <SelectValue placeholder="Pick a time" />
                  <Clock size={16} className="text-lime shrink-0" />
                </SelectTrigger>
                <SelectContent className="bg-surface border-white/10 text-white rounded-none max-h-64">
                  {TIME_SLOTS.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`booking-time-opt-${t.replace(/[: ]/g, "")}`} className="font-mono text-sm">
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="sm:col-span-2">
              <label className="overline block mb-2">Notes</label>
              <textarea data-testid="booking-message" rows={3} value={form.message} onChange={update("message")} className="clinical-input resize-none" placeholder="Anything we should know about the job or location?" />
            </div>

            <div className="sm:col-span-2 flex justify-end">
              <button
                type="submit"
                data-testid="booking-submit"
                disabled={submitting}
                className="btn-fill border border-lime text-lime px-10 py-4 font-mono text-xs uppercase tracking-[0.2em] flex items-center gap-3 disabled:opacity-50"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {submitting ? "Sending" : "Submit Request"}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
